// Qué empresas tienen el servicio suspendido — cache compartido para toda la app.
//
// El helpdesk muestra una empresa en ~36 pantallas distintas. Cablear el aviso pantalla por
// pantalla garantiza olvidarse de varias, y que la próxima que se escriba nazca sin él. En
// vez de eso hay UNA lista, cacheada acá, que `CompanyLogo` consulta — y como ese componente
// lo usan las 36, el aviso viaja solo.
//
// La lista es diminuta (los clientes suspendidos son pocos y es sólo id + nombre + fecha),
// así que traerla entera y filtrar en memoria es más barato que consultar por empresa.
import api from './api';

const TTL_MS = 5 * 60 * 1000;
let cache = null;        // { vence, porId: Map, porNombre: Map }
let enVuelo = null;
const suscriptores = new Set();

function indexar(filas) {
  const porId = new Map(), porNombre = new Map();
  for (const f of filas) {
    const info = { id: f.id, name: f.fantasy_name || f.name, since: f.since, note: f.note };
    porId.set(f.id, info);
    // Se indexa por los DOS nombres porque cada pantalla muestra el que tiene a mano
    // (unas el de fantasía, otras el legal) y no todas pasan el id.
    for (const n of [f.name, f.fantasy_name]) if (n) porNombre.set(n.trim().toLowerCase(), info);
  }
  return { vence: Date.now() + TTL_MS, porId, porNombre };
}

function cargar() {
  if (cache && Date.now() < cache.vence) return Promise.resolve(cache);
  if (enVuelo) return enVuelo;
  enVuelo = api.get('/companies/suspended')
    .then(r => { cache = indexar(r.data || []); suscriptores.forEach(fn => fn()); return cache; })
    .catch(() => {
      // Si falla, se sigue mostrando lo último que supimos (o nada). Un error de red no
      // puede romper 36 pantallas por un badge.
      cache = cache || indexar([]);
      cache.vence = Date.now() + 60 * 1000;   // reintento pronto
      return cache;
    })
    .finally(() => { enVuelo = null; });
  return enVuelo;
}

/** Fuerza el refresco tras suspender/reactivar, para que el badge aparezca o desaparezca ya. */
export function invalidarSuspendidas() {
  cache = null;
  cargar().then(() => suscriptores.forEach(fn => fn()));
}

export function buscarSuspendida({ companyId, name } = {}) {
  if (!cache) return null;
  if (companyId && cache.porId.has(companyId)) return cache.porId.get(companyId);
  if (name && cache.porNombre.has(String(name).trim().toLowerCase())) return cache.porNombre.get(String(name).trim().toLowerCase());
  return null;
}

export function suscribir(fn) { suscriptores.add(fn); return () => suscriptores.delete(fn); }

/** Asegura que la lista esté cargada. El portal del cliente NO la pide: es un endpoint
 *  interno y además un cliente no tiene por qué ver esta información sobre sí mismo. */
export function asegurarCargada() {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/portal')) return;
  cargar();
}
